import { getStaticCDN } from '@design-system/utils/utils';
import { TRegionKey } from '@source/interface';

export type AboutContentType =
  | 'aboutCarro'
  | 'aboutMission'
  | 'aboutVision'
  | 'aboutAward'
  | 'keyMilestones'
  | 'ourSubsidiaries';

export const getContentAboutDesc = (contentType: AboutContentType, region: TRegionKey, descCount: number) => {
  const arr = [];
  for (let i = 1; i <= descCount; i += 1) {
    arr.push(`aboutUs:aboutUs.${contentType}.${region}.desc${i}`);
  }

  return arr;
};

export const getTimelineContent = (contentType: AboutContentType, region: TRegionKey, descCount = 20) => {
  const arr = [];
  for (let i = 1; i <= descCount; i += 1) {
    arr.push({
      time: `aboutUs:aboutUs.${contentType}.${region}.desc${i}.time`,
      descMain: `aboutUs:aboutUs.${contentType}.${region}.desc${i}.descMain`,
      descSub: `aboutUs:aboutUs.${contentType}.${region}.desc${i}.descSub`,
    });
  }

  return arr;
};

export const aboutUsContentByCountry: Partial<
  Record<
    TRegionKey,
    {
      title: string;
      content: AboutContentType;
      descNumber?: number;
      type: 'paragraph' | 'timeline';
    }[]
  >
> = {
  jp: [
    {
      title: 'aboutUs.titleAboutCarro',
      content: 'aboutCarro',
      type: 'paragraph',
    },
    {
      title: 'aboutUs.titleKeyMilestones',
      content: 'keyMilestones',
      type: 'timeline',
    },
    {
      title: 'aboutUs.titleOurSubsidiaries',
      content: 'ourSubsidiaries',
      type: 'timeline',
    },
    {
      title: 'aboutUs.titleAboutAward',
      content: 'aboutAward',
      type: 'timeline',
    },
  ],
  hk: [
    {
      title: 'aboutUs.titleAboutCarro',
      content: 'aboutCarro',
      type: 'paragraph',
    },
    {
      title: 'aboutUs.titleKeyMilestones',
      content: 'keyMilestones',
      type: 'timeline',
    },
    {
      title: 'aboutUs.titleOurSubsidiaries',
      content: 'ourSubsidiaries',
      type: 'timeline',
    },
    {
      title: 'aboutUs.titleAboutAward',
      content: 'aboutAward',
      type: 'timeline',
    },
  ],
};

export const contentAwards = [
  {
    url: 'https://www.ft.com/content/36b37f3b-dfcd-4630-a191-d4a3e2bcdf0b',
    title: 'aboutUs.awards.nikkei',
    desc: '2021',
    image: getStaticCDN('/assets/shared/about-us/awards-nikkei-asia-high-growth-companies-2021.png'),
    alt: 'carro tech nikkei-ft-statista high-growth company asia-pacific 2021',
  },
  {
    url: 'https://www.straitstimes.com/business/companies-markets/carro-tech-innovation-drives-online-car-portal-to-the-top',
    title: 'aboutUs.awards.straitstimes',
    desc: '2021',
    image: getStaticCDN('/assets/shared/about-us/awards-fastest-growing-company-2021.png'),
    alt: 'carro straits Times No. 1 Singapore’s Fastest Growing Company',
  },
  {
    url: 'https://awards.marketing-interactive.com/pr-awards-sg/winners',
    title: 'aboutUs.awards.marketingPR',
    desc: '2020',
    image: getStaticCDN('/assets/shared/about-us/social-silver.png'),
    alt: "carro marketing's PR Awards",
  },
  {
    url: 'https://sbr.com.sg/markets-investing/in-focus/deeptech-dominates-startup-landscape',
    title: 'aboutUs.awards.sbr',
    desc: '2019',
    image: getStaticCDN('/assets/shared/about-us/carro-singapore-business-reviews-hottest-startup-2019.png'),
    alt: "carro singapore business review's hottest startup 2019",
  },
  {
    url: 'https://awards.marketing-interactive.com/mobex-awards-sg/2019-winners',
    title: 'aboutUs.awards.mobEx',
    desc: '2019',
    image: getStaticCDN('/assets/shared/about-us/carro-silver-mob-ex-best-e-commerce-app-2019.png'),
    alt: 'carro silver mob ex best e commerce app 2019',
  },
  {
    url: 'https://carro.sg/blog/carro-vc-deal-of-the-year-award-2018/',
    title: 'aboutUs.awards.svca',
    desc: '2018',
    image: getStaticCDN('/assets/shared/about-us/carro-svca-vc-deal-of-the-year-2018.png'),
    alt: 'carro svca vc deal of the year 2018',
  },
  {
    url: 'https://www.businesstimes.com.sg/companies-markets/carro-simplifies-car-buying-selling',
    title: 'aboutUs.awards.emerging',
    desc: '2016',
    image: getStaticCDN('/assets/shared/about-us/carro-emerging-enterprise-award-2016-finalist.png'),
    alt: 'carro emerging enterprise award 2016 finalist',
  },
];

export const contentAboutNews = [
  {
    url: 'https://www.businesstimes.com.sg/garage/carro-eyes-digibank-licence-as-vehicle-to-expand-into-sme-loans',
    image: getStaticCDN('/assets/shared/about-us/carro-automotive-marketplace-in-the-business-times.jpeg'),
    alt: 'carro automotive marketplace in the business times',
    title: 'Carro eyes digibank licence as vehicle to expand into SME loans',
    desc: 'Carro is now eyeing a wholesale digital banking licence to expand the business.',
    logo: getStaticCDN('/assets/shared/about-us/logo_the_business_times.png'),
  },
  {
    url: 'https://www.entrepreneur.com/article/337808',
    image: getStaticCDN('/assets/shared/about-us/carro-car-marketplace-in-entrepreneur-asia-pacific.jpg'),
    alt: 'carro car marketplace in entrepreneur asia pacific',
    title: "This Start-up is on an Acquisition Spree to Expand into Southeast Asia's Used Car Marketplace",
    desc: "Carro announces the acquisition of Indonesia's e-classified marketplace, Jualo",
    logo: getStaticCDN('/assets/shared/about-us/logo_entrepreneur_ap.png'),
  },
  {
    url: 'https://topgear.com.sg/node/11044',
    image: getStaticCDN('/assets/shared/about-us/carro-automotive-marketplace-in-top-gear.jpg'),
    alt: 'carro automotive marketplace in top gear',
    title: 'Carro launches subscription-based car ownership service in Singapore',
    desc: "Carro launches Singapore's first subscription-based car ownership service",
    logo: getStaticCDN('/assets/shared/about-us/logo_topgear.png'),
  },
];
